// CMS Commissions - Manager commission tracking and payout management
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DollarSign, Download, CheckCircle, Clock, TrendingUp, Banknote, FileText, Send, ReceiptText, CreditCard, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useCMS } from './CMSContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CMSCommissions = () => {
  const { token } = useCMS();
  const [commissions, setCommissions] = useState([]);
  const [managers, setManagers] = useState([]);
  const [months, setMonths] = useState([]);
  const [managerSummary, setManagerSummary] = useState([]);
  const [managerFilter, setManagerFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [grandTotal, setGrandTotal] = useState(0);
  const [grandPending, setGrandPending] = useState(0);
  const [grandPaidOut, setGrandPaidOut] = useState(0);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payoutDialog, setPayoutDialog] = useState(null);
  const [processingPayout, setProcessingPayout] = useState(false);
  const [reports, setReports] = useState([]);
  const [updatingReport, setUpdatingReport] = useState(null);

  useEffect(() => {
    fetchCommissions();
    fetchPayouts();
    fetchReports();
  }, [managerFilter, monthFilter, statusFilter]);

  const fetchCommissions = async () => {
    try {
      let url = `${API}/cms/commissions?`;
      if (managerFilter) url += `&manager=${encodeURIComponent(managerFilter)}`;
      if (monthFilter) url += `&month=${encodeURIComponent(monthFilter)}`;
      if (statusFilter) url += `&status=${statusFilter}`;

      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setCommissions(res.data.commissions || []);
      setManagers(res.data.managers || []);
      setMonths(res.data.months || []);
      setManagerSummary(res.data.manager_summary || []);
      setGrandTotal(res.data.grand_total || 0);
      setGrandPending(res.data.grand_pending || 0);
      setGrandPaidOut(res.data.grand_paid_out || 0);
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayouts = async () => {
    try {
      const res = await axios.get(`${API}/cms/commissions/payouts`, { headers: { Authorization: `Bearer ${token}` } });
      setPayouts(res.data.payouts || []);
    } catch (error) {
      console.error('Failed to fetch payouts:', error);
    }
  };

  const handlePayout = async (managerEmail, month) => {
    setProcessingPayout(true);
    try {
      const res = await axios.post(`${API}/cms/commissions/payout`, {
        manager_email: managerEmail,
        month: month || undefined
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.success) {
        toast.success(`Payout completed: €${res.data.total_amount.toFixed(2)} for ${res.data.payout.manager_name}`);
        setPayoutDialog(null);
        fetchCommissions();
        fetchPayouts();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payout failed');
    } finally {
      setProcessingPayout(false);
    }
  };

  const handleExportCSV = () => {
    const headers = ['Manager', 'Email', 'Business', 'Pass Type', 'Qty', 'Unit Price', 'Percentage', 'Commission', 'Month', 'Status', 'Created'];
    const rows = commissions.map(c => [
      c.manager_name, c.manager_email, c.business_name, c.pass_type,
      c.quantity, c.unit_price || '', `${((c.percentage || 0.20) * 100).toFixed(0)}%`, c.total_commission, c.month, c.status,
      new Date(c.created_at).toLocaleDateString()
    ]);
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commissions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API}/api/cms/commissions/reports`, { headers: { Authorization: `Bearer ${token}` } });
      setReports(res.data.reports || []);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    }
  };

  const updateReportStatus = async (reportId, status) => {
    setUpdatingReport(reportId);
    try {
      await axios.put(`${API}/api/cms/commissions/reports/${reportId}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Report status updated to: ${status}`);
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Status update failed');
    } finally {
      setUpdatingReport(null);
    }
  };

  const triggerMonthlyReports = async () => {
    try {
      const res = await axios.post(`${API}/api/cms/commissions/reports/run-monthly`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Monthly reports: ${res.data.reports_sent} sent to ${res.data.managers} managers`);
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Report generation failed');
    }
  };

  const reportStatusBadge = (status) => {
    const config = {
      generated: { label: 'Generated', variant: 'outline', icon: FileText },
      sent: { label: 'Verzonden', variant: 'secondary', icon: Send },
      invoice_received: { label: 'Factuur Ontvangen', variant: 'default', icon: ReceiptText },
      paid: { label: 'Betaald', variant: 'default', icon: CheckCircle },
    };
    const c = config[status] || config.generated;
    const Icon = c.icon;
    return <Badge variant={c.variant} className={status === 'paid' ? 'bg-green-600' : status === 'invoice_received' ? 'bg-blue-600' : ''}><Icon className="w-3 h-3 mr-1" />{c.label}</Badge>;
  };

  const handlePrintReport = () => {
    const printHTML = `
      <!DOCTYPE html><html><head><title>Commission Report</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
        h1 { color: #1a73e8; } h2 { color: #555; margin-top: 30px; }
        table { width: 100%; border-collapse: collapse; margin: 15px 0; }
        th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
        th { background: #f5f5f5; font-weight: 600; }
        .total { font-weight: bold; font-size: 18px; color: #1a73e8; }
        .stats { display: flex; gap: 20px; margin: 20px 0; }
        .stat-card { background: #f0f7ff; padding: 15px 25px; border-radius: 8px; }
        .footer { margin-top: 40px; text-align: center; color: #999; font-size: 11px; }
      </style></head><body>
        <h1>Freestays - Commission Report</h1>
        <p>Generated: ${new Date().toLocaleDateString()} ${managerFilter ? `| Manager: ${managers.find(m => m.email === managerFilter)?.name || managerFilter}` : ''} ${monthFilter ? `| Month: ${monthFilter}` : ''}</p>
        <div class="stats">
          <div class="stat-card"><strong>Total</strong><br><span class="total">&euro;${grandTotal.toFixed(2)}</span></div>
          <div class="stat-card"><strong>Pending</strong><br><span class="total">&euro;${grandPending.toFixed(2)}</span></div>
          <div class="stat-card"><strong>Paid Out</strong><br><span class="total">&euro;${grandPaidOut.toFixed(2)}</span></div>
        </div>
        <h2>Per Manager</h2>
        <table>
          <thead><tr><th>Manager</th><th>Vouchers</th><th>Passes</th><th>Total Commission</th><th>Pending</th><th>Paid Out</th></tr></thead>
          <tbody>${managerSummary.map(m => `
            <tr><td>${m.manager_name}</td><td>${m.vouchers} x 20%</td><td>${m.passes} x 20%</td><td>&euro;${m.total.toFixed(2)}</td><td>&euro;${m.pending.toFixed(2)}</td><td>&euro;${m.paid_out.toFixed(2)}</td></tr>
          `).join('')}</tbody>
        </table>
        <h2>Detail</h2>
        <table>
          <thead><tr><th>Manager</th><th>Business</th><th>Type</th><th>Qty</th><th>Commission</th><th>Month</th><th>Status</th></tr></thead>
          <tbody>${commissions.map(c => `
            <tr><td>${c.manager_name}</td><td>${c.business_name}</td><td>${c.pass_type}</td><td>${c.quantity}</td><td>&euro;${c.total_commission.toFixed(2)}</td><td>${c.month}</td><td>${c.status}</td></tr>
          `).join('')}</tbody>
        </table>
        <div class="footer"><p>Freestays B.V. - Waanderweg 50a, Emmen | All amounts incl. BTW</p></div>
      </body></html>
    `;
    const w = window.open('', '_blank');
    w.document.write(printHTML);
    w.document.close();
    w.onload = () => w.print();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Commissions</h1>
          <p className="text-muted-foreground">Manager commission tracking and payouts</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportCSV} data-testid="commission-export-csv">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview"><DollarSign className="w-4 h-4 mr-1.5" />Overview</TabsTrigger>
          <TabsTrigger value="reports"><FileText className="w-4 h-4 mr-1.5" />Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-4">

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Commission</p>
              <p className="text-2xl font-bold text-green-600">€{grandTotal.toFixed(2)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Pending Payout</p>
              <p className="text-2xl font-bold text-amber-600">€{grandPending.toFixed(2)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
              <CheckCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Paid Out</p>
              <p className="text-2xl font-bold text-blue-600">€{grandPaidOut.toFixed(2)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Managers</p>
              <p className="text-2xl font-bold text-purple-600">{managers.length}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <Select value={managerFilter || 'all'} onValueChange={(v) => setManagerFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder="All Managers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Managers</SelectItem>
            {managers.map(m => (
              <SelectItem key={m.email} value={m.email}>{m.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={monthFilter || 'all'} onValueChange={(v) => setMonthFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All Months" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Months</SelectItem>
            {months.map(m => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paid_out">Paid Out</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Manager Summary */}
      {managerSummary.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5" /> Per Manager Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Manager</TableHead>
                  <TableHead>Vouchers Sold</TableHead>
                  <TableHead>Passes Sold</TableHead>
                  <TableHead>Total Commission</TableHead>
                  <TableHead>Pending</TableHead>
                  <TableHead>Paid Out</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {managerSummary.map(m => (
                  <TableRow key={m.manager_email}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{m.manager_name}</p>
                        <p className="text-xs text-muted-foreground">{m.manager_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>{m.vouchers} x €6</TableCell>
                    <TableCell>{m.passes} x €30</TableCell>
                    <TableCell className="font-medium text-green-600">€{m.total.toFixed(2)}</TableCell>
                    <TableCell className="text-amber-600">€{m.pending.toFixed(2)}</TableCell>
                    <TableCell className="text-blue-600">€{m.paid_out.toFixed(2)}</TableCell>
                    <TableCell>
                      {m.pending > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => setPayoutDialog(m)}
                          data-testid={`payout-${m.manager_email}`}
                        >
                          <Banknote className="w-4 h-4 mr-1" /> Payout €{m.pending.toFixed(2)}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Commission Detail Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Commission Details</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Manager</TableHead>
                <TableHead>Business</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Qty</TableHead>
                <TableHead>Stukprijs</TableHead>
                <TableHead>%</TableHead>
                <TableHead>Netto</TableHead>
                <TableHead>BTW</TableHead>
                <TableHead>Totaal</TableHead>
                <TableHead>Maand</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {commissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                    No commissions found. Commissions are auto-generated when B2B orders are marked as paid.
                  </TableCell>
                </TableRow>
              ) : (
                commissions.map(c => (
                  <TableRow key={c.commission_id}>
                    <TableCell>
                      <p className="font-medium text-sm">{c.manager_name}</p>
                    </TableCell>
                    <TableCell className="text-sm">{c.business_name}</TableCell>
                    <TableCell className="text-sm">{c.pass_type === 'annual' ? 'Annual Pass' : 'Voucher'}</TableCell>
                    <TableCell className="text-sm">{c.quantity}</TableCell>
                    <TableCell className="text-sm">€{(c.unit_price || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-sm">{((c.percentage || 0.20) * 100).toFixed(0)}%</TableCell>
                    <TableCell className="text-sm">€{(c.commission_netto || c.total_commission || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">€{(c.commission_btw || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-sm font-medium text-green-600">€{c.total_commission?.toFixed(2)}</TableCell>
                    <TableCell className="text-sm">{c.month}</TableCell>
                    <TableCell>
                      {c.status === 'paid_out' ? (
                        <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Paid Out</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Pending</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Payout History */}
      {payouts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Banknote className="w-5 h-5" /> Payout History
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Manager</TableHead>
                  <TableHead>Month</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Commissions</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payouts.map(p => (
                  <TableRow key={p.payout_id}>
                    <TableCell className="font-medium">{p.manager_name}</TableCell>
                    <TableCell>{p.month}</TableCell>
                    <TableCell className="font-medium text-green-600">€{p.total_amount?.toFixed(2)}</TableCell>
                    <TableCell>{p.commission_count} items</TableCell>
                    <TableCell className="text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Payout Confirmation Dialog */}
      <Dialog open={!!payoutDialog} onOpenChange={() => setPayoutDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-green-600" />
              Confirm Payout
            </DialogTitle>
          </DialogHeader>
          {payoutDialog && (
            <div className="space-y-4">
              <div className="p-4 bg-secondary/50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Manager</span>
                  <span className="font-medium">{payoutDialog.manager_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span>{payoutDialog.manager_email}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Vouchers</span>
                  <span>{payoutDialog.vouchers} x €6,00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Passes</span>
                  <span>{payoutDialog.passes} x €30,00</span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total Payout (incl. BTW)</span>
                  <span className="text-green-600">€{payoutDialog.pending?.toFixed(2)}</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                This will mark all pending commissions as paid out. A separate bank transfer must be made from the Freestays bank account to the manager.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPayoutDialog(null)}>Cancel</Button>
                <Button
                  onClick={() => handlePayout(payoutDialog.manager_email)}
                  disabled={processingPayout}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="confirm-payout-btn"
                >
                  {processingPayout ? 'Processing...' : `Confirm Payout €${payoutDialog.pending?.toFixed(2)}`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-6 mt-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Maandelijkse Commissie Rapporten</h2>
              <p className="text-sm text-muted-foreground">Periode: 20e vorige maand t/m 20e huidige maand. Verzonden op de 21e.</p>
            </div>
            <Button onClick={triggerMonthlyReports} variant="outline" data-testid="trigger-monthly-reports">
              <Send className="w-4 h-4 mr-2" /> Generate Monthly Reports
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Manager</TableHead>
                    <TableHead>Periode</TableHead>
                    <TableHead className="text-right">Omzet</TableHead>
                    <TableHead className="text-right">Provisie</TableHead>
                    <TableHead className="text-right">BTW</TableHead>
                    <TableHead className="text-right">Totaal</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Acties</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        Geen rapporten gevonden. Klik op "Generate Monthly Reports" om rapporten te genereren.
                      </TableCell>
                    </TableRow>
                  ) : reports.map((report) => (
                    <TableRow key={report.report_id}>
                      <TableCell>
                        <div>
                          <span className="font-medium">{report.manager_name}</span>
                          <p className="text-xs text-muted-foreground">{report.manager_email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{report.period_start} — {report.period_end}</TableCell>
                      <TableCell className="text-right font-medium">€{report.total_revenue?.toFixed(2)}</TableCell>
                      <TableCell className="text-right">€{report.commission_netto?.toFixed(2)}</TableCell>
                      <TableCell className="text-right">€{report.commission_btw?.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-bold">€{report.commission_total?.toFixed(2)}</TableCell>
                      <TableCell>{reportStatusBadge(report.status)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {report.status === 'sent' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateReportStatus(report.report_id, 'invoice_received')}
                              disabled={updatingReport === report.report_id}
                              data-testid={`report-invoice-received-${report.report_id}`}
                            >
                              <ReceiptText className="w-3.5 h-3.5 mr-1" /> Factuur Ontvangen
                            </Button>
                          )}
                          {report.status === 'invoice_received' && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => updateReportStatus(report.report_id, 'paid')}
                              disabled={updatingReport === report.report_id}
                              data-testid={`report-mark-paid-${report.report_id}`}
                            >
                              <CreditCard className="w-3.5 h-3.5 mr-1" /> Betaald
                            </Button>
                          )}
                          {report.status === 'paid' && (
                            <span className="text-xs text-green-600 flex items-center gap-1">
                              <CheckCircle className="w-3.5 h-3.5" /> {report.paid_at ? new Date(report.paid_at).toLocaleDateString('nl-NL') : 'Betaald'}
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CMSCommissions;
